import { useState } from 'react';
import { useApp } from '../context/AppContext';
import { getFamilyColor, getInitials } from '../utils/helpers';
import { Modal } from '../components/UI';

export default function Families() {
  const { families, addMember, updateMember, removeMember, updateFamilyName, showToast } = useApp();
  const [modal, setModal] = useState(null); // { type: 'member', familyId, memberId? }

  const handleRenameFamily = (familyId) => {
    const family = families[familyId];
    const newName = window.prompt('Enter new family name:', family.name);
    if (newName?.trim()) {
      updateFamilyName(familyId, newName.trim());
      showToast('Family renamed', 'success');
    }
  };

  const handleDeleteMember = (familyId, memberId, memberName) => {
    if (window.confirm(`Remove ${memberName} from the family?`)) {
      removeMember(familyId, memberId);
      showToast(`${memberName} removed`, 'info');
    }
  };

  const openAddMember = (familyId) => setModal({ type: 'member', familyId });
  const openEditMember = (familyId, memberId) => setModal({ type: 'member', familyId, memberId });
  const closeModal = () => setModal(null);

  const editingMember = modal?.memberId
    ? families[modal.familyId]?.members.find(m => m.id === modal.memberId)
    : null;

  const handleMemberSubmit = async (e) => {
    e.preventDefault();
    const name = e.target.name.value.trim();
    const age  = parseInt(e.target.age.value);
    if (!name) return;
    if (editingMember) {
      await updateMember(modal.familyId, modal.memberId, { name, age });
      showToast(`${name} updated`, 'success');
    } else {
      await addMember(modal.familyId, { name, age });
      showToast(`${name} added`, 'success');
    }
    closeModal();
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Family Setup</h1>
          <p className="page-subtitle">Manage trip participants and family members</p>
        </div>
      </div>

      <div className="family-grid">
        {['family1', 'family2'].map(fid => {
          const family = families[fid];
          if (!family) return null;
          const colors = getFamilyColor(fid);
          return (
            <div className="glass-card family-card" key={fid} style={{ borderTop: `3px solid ${colors.border}` }}>
              <div className="family-card-header">
                <div className="family-title-row">
                  <span className="family-badge" style={{ background: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}>
                    {family.members.length} members
                  </span>
                  <h2 className="family-name">{family.name}</h2>
                  <button className="btn-icon btn-rename-family" title="Rename" onClick={() => handleRenameFamily(fid)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                </div>
              </div>

              <div className="members-list">
                {family.members.map(m => (
                  <div className="member-card" key={m.id}>
                    <div className="member-avatar" style={{ background: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}>
                      {getInitials(m.name)}
                    </div>
                    <div className="member-info">
                      <span className="member-name">{m.name}</span>
                      <span className="member-age">{m.age} yrs</span>
                    </div>
                    <div className="member-actions">
                      <button className="btn-icon" title="Edit" onClick={() => openEditMember(fid, m.id)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      <button className="btn-icon" title="Remove" onClick={() => handleDeleteMember(fid, m.id, m.name)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button className="btn btn-outline btn-add-member" onClick={() => openAddMember(fid)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add Member
              </button>
            </div>
          );
        })}
      </div>

      {/* Member Modal */}
      <Modal isOpen={!!modal} onClose={closeModal} title={editingMember ? 'Edit Member' : 'Add Member'}>
        <form onSubmit={handleMemberSubmit}>
          <div className="form-group">
            <label className="form-label">Name</label>
            <input name="name" type="text" className="form-input" defaultValue={editingMember?.name || ''} required placeholder="Enter name" autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">Age</label>
            <input name="age" type="number" className="form-input" defaultValue={editingMember?.age || ''} required min="0" max="120" placeholder="Enter age" />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={closeModal}>Cancel</button>
            <button type="submit" className="btn btn-primary">{editingMember ? 'Update' : 'Add'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
